import React from 'react';
import { motion } from 'framer-motion';
import { FaCloud, FaMicrosoft, FaAws, FaGoogle, FaCheckCircle, FaMobileAlt, FaExchangeAlt } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import CTABanner from '../components/CTABanner';

const CloudMigration = ({ market }) => {
  const b = getBrand(market);
  const ctaLink = market === 'lu' ? '/services/assessment' : '/contact';

  // 3 infra platforms → 3-col grid
  const infra = [
    { icon: <FaAws size={44} color="#FF9900" />,     name: 'AWS',           desc: 'Scalable cloud infrastructure, compute, storage, and databases.',           best: 'Custom applications, complex workloads, global scale' },
    { icon: <FaMicrosoft size={44} color="#00A4EF" />, name: 'Microsoft Azure', desc: 'Enterprise cloud with seamless Microsoft ecosystem integration.',           best: 'Windows workloads, hybrid cloud, Microsoft 365' },
    { icon: <FaGoogle size={44} color="#4285F4" />,   name: 'Google Cloud',  desc: 'Data analytics, machine learning, and containerised applications.',          best: 'AI/ML projects, data workloads, Kubernetes' }
  ];

  // 2 collab platforms → 2-col grid
  const collab = [
    { icon: <FaMicrosoft size={44} color="#D83B01" />, name: 'Microsoft 365',   desc: 'Outlook, Teams, SharePoint, OneDrive — complete productivity suite.',       best: 'Email, collaboration, and document management' },
    { icon: <FaGoogle size={44} color="#34A853" />,   name: 'Google Workspace', desc: 'Gmail, Meet, Drive, Docs — cloud-native real-time collaboration.',         best: 'Teams prioritising simplicity and live co-editing' }
  ];

  // 6 benefits → 3-col grid
  const benefits = [
    'Reduce IT infrastructure costs by 30–50%',
    'Access your data from anywhere, anytime',
    'Automatic updates and maintenance',
    'Better security and compliance posture',
    'Scale instantly — no hardware investments',
    'Built-in business continuity and disaster recovery'
  ];

  // 4 process steps → 2×2
  const process = [
    { step: '01', title: 'Assessment',   description: 'Evaluate current infrastructure, applications, and data landscape.' },
    { step: '02', title: 'Planning',     description: 'Design migration strategy, timeline, and risk mitigation plan.' },
    { step: '03', title: 'Migration',    description: 'Move data and applications with minimal downtime.' },
    { step: '04', title: 'Optimisation', description: 'Fine-tune performance and train your team for cloud operations.' }
  ];

  // Hybrid cloud features
  const hybridFeatures = [
    'Gradual migration — move at your own pace',
    'Keep legacy applications on-premise',
    'Cloud-based disaster recovery and backup',
    'Secure connectivity between environments',
    'Cost optimisation — cloud where it makes sense',
    'Compliance and data sovereignty'
  ];

  // MDM features
  const mdmFeatures = [
    'Microsoft Intune / Endpoint Manager',
    'Omnissa WorkspaceONE (formerly VMware)',
    'Zero-touch device deployment',
    'Remote wipe and security policies',
    'App deployment and updates',
    'BYOD (Bring Your Own Device) support'
  ];

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title="Cloud Migration Services"
        subtitle="Move to the cloud with minimal disruption. We handle the complexity so you can focus on your business."
        ctaText="Plan Your Migration"
        ctaLink={ctaLink}
        icon={<FaCloud color="#1A237E" />}
      />

      {/* ── INFRASTRUCTURE PLATFORMS — 3 cols ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Infrastructure</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '10px' }}>
              Cloud infrastructure platforms
            </h2>
            <p style={{ color: b.textMuted, maxWidth: '500px', margin: '0 auto', fontSize: b.fz.base }}>
              Host applications, databases, and workloads in the cloud
            </p>
          </div>
          <div className="grid-3">
            {infra.map((p, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px', textAlign: 'center' }}
              >
                <div style={{ marginBottom: '18px' }}>{p.icon}</div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{p.name}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted, marginBottom: '12px' }}>{p.desc}</p>
                <p style={{ fontSize: b.fz.xs, color: b.isMWO ? 'rgba(232,228,220,0.62)' : '#94a3b8', fontStyle: 'italic' }}>
                  Best for: {p.best}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COLLAB PLATFORMS — 2 cols ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Collaboration</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '10px' }}>
              Collaboration & productivity platforms
            </h2>
            <p style={{ color: b.textMuted, maxWidth: '480px', margin: '0 auto', fontSize: b.fz.base }}>
              Email, document sharing, video conferencing, and team collaboration
            </p>
          </div>
          <div className="grid-2">
            {collab.map((p, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px', textAlign: 'center' }}
              >
                <div style={{ marginBottom: '18px' }}>{p.icon}</div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{p.name}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted, marginBottom: '12px' }}>{p.desc}</p>
                <p style={{ fontSize: b.fz.xs, color: b.isMWO ? 'rgba(232,228,220,0.62)' : '#94a3b8', fontStyle: 'italic' }}>
                  Best for: {p.best}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MODERN DEVICE MANAGEMENT ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <FaMobileAlt size={40} color={b.accent} style={{ marginBottom: '16px' }} />
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Device Management</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '10px' }}>
              Modern Device Management
            </h2>
            <p style={{ color: b.textMuted, maxWidth: '520px', margin: '0 auto', fontSize: b.fz.base }}>
              Manage and secure all devices — laptops, phones, tablets — from the cloud
            </p>
          </div>
          {/* 6 features → 3-col */}
          <div className="grid-6">
            {mdmFeatures.map((f, i) => (
              <div key={i} className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}>
                <FaCheckCircle size={16} color={b.accent} className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HYBRID CLOUD ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <FaExchangeAlt size={36} color={b.accent} style={{ marginBottom: '14px' }} />
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Best of Both Worlds</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '10px' }}>
              Hybrid cloud solutions
            </h2>
            <p style={{ color: b.textMuted, maxWidth: '560px', margin: '0 auto', fontSize: b.fz.base }}>
              Combine on-premise and cloud infrastructure for maximum flexibility and compliance
            </p>
          </div>
          <div className="grid-2">
            <div style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 28px' }}>
              <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '20px' }}>
                Hybrid Cloud Features
              </h3>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {hybridFeatures.map((f, i) => (
                  <li key={i} style={{ display: 'flex', gap: '10px', fontSize: b.fz.sm, color: b.textMuted, alignItems: 'flex-start' }}>
                    <span style={{ color: b.accent, flexShrink: 0, marginTop: '2px' }}>→</span> {f}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 28px' }}>
              <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '20px' }}>
                Ideal for
              </h3>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  'Regulated industries requiring on-premise data',
                  'Legacy applications not ready for cloud migration',
                  'Gradual digital transformation journeys',
                  'Optimising costs by keeping some workloads local'
                ].map((u, i) => (
                  <li key={i} style={{ display: 'flex', gap: '10px', fontSize: b.fz.sm, color: b.textMuted, alignItems: 'flex-start' }}>
                    <span style={{ color: b.accent, flexShrink: 0, marginTop: '2px' }}>→</span> {u}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS — 6 → 3-col ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Key Benefits</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Why move to the cloud?
            </h2>
          </div>
          <div className="grid-6">
            {benefits.map((benefit, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }} viewport={{ once: true }}
                className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}
              >
                <FaCheckCircle size={16} color={b.accent} className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6, fontWeight: 500 }}>{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS — 4 → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Our Process</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Our migration process
            </h2>
          </div>
          <div className="grid-4">
            {process.map((phase, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, delay: i * 0.07 }} viewport={{ once: true }}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px', textAlign: 'center' }}
              >
                <div className="process-step-num" style={{ background: b.stepBg, border: b.stepBorder, color: b.stepColor }}>
                  {phase.step}
                </div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{phase.title}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted }}>{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <CTABanner
        market={market}
        title="Ready to move to the cloud?"
        body={market === 'lu'
          ? 'Schedule a free assessment and get a customised migration plan for your Luxembourg business.'
          : "Let's discuss your migration strategy and create a smooth transition plan."}
        ctaText={market === 'lu' ? 'Get Free Assessment' : 'Start Planning'}
        ctaLink={ctaLink}
      />
    </div>
  );
};

export default CloudMigration;
